import { resolveBootstrap, BootstrapError } from './bootstrap';
import { now as serverNow } from './clock';
import { readBootstrapConfig } from './config';
import { buildMockOrders } from './mocks/mock-orders';
import { OrderStore } from './orders/store';
import { startLiveRuntime } from './runtime';
import { captureException, initSentry } from './sentry';
import type { ConnectionState } from './types';
import { createRenderer, renderErrorPanel, renderLoading } from './ui/render';
import { startVersionWatcher } from './version';

const root = document.getElementById('app');
if (!root) {
  // Without #app we have no way to show anything; bail loudly.
  throw new Error('Root element #app not found');
}

initSentry();

// Read once at startup: VITE_* config is baked in at build time, so the
// resulting values are stable for the lifetime of the bundle.
const config = readBootstrapConfig();

// Global error nets - per §2.10 every uncaught browser exception goes to
// Sentry and renders the unexpected-error panel.
window.addEventListener('error', (ev) => {
  captureException(ev.error ?? ev.message, { stage: 'window.onerror' });
  renderErrorPanel(
    root!,
    {
      title: 'Unexpected Error',
      body: 'Something went wrong. The screen will retry shortly.',
      detail: process.env.NODE_ENV !== 'production' ? String(ev.error ?? ev.message) : undefined,
    },
    config.showErrorDetail,
  );
});

window.addEventListener('unhandledrejection', (ev) => {
  captureException(ev.reason, { stage: 'unhandledrejection' });
});

async function boot(): Promise<void> {
  renderLoading(root!, '');

  let bootstrapResult;
  try {
    bootstrapResult = await resolveBootstrap(config);
  } catch (err) {
    captureException(err, { stage: 'bootstrap' });
    const isBootstrap = err instanceof BootstrapError;
    renderErrorPanel(
      root!,
      {
        title: 'Temporarily Unavailable',
        body: isBootstrap
          ? 'This screen is missing required configuration. Please contact crew or IT support.'
          : 'We could not reach the store services. Please contact crew or IT support.',
        detail: err instanceof Error ? err.message : String(err),
      },
      config.showErrorDetail,
    );
    return;
  }

  const { settings, xApiKey } = bootstrapResult;
  const store = new OrderStore({
    doneRetentionMinutes: config.doneRetentionMinutes,
    sortBy: config.sortBy,
    // Retention and lead-time decisions run on server time, not the TV clock.
    now: serverNow,
  });
  const render = createRenderer(root!, { scroll: config.scroll });

  let connection: ConnectionState = config.mock ? 'online' : 'offline';
  let loading = !config.mock;

  store.subscribe((snap) => {
    render({
      connection,
      storeName: settings.storeName,
      left: snap.left,
      right: snap.right,
      loading,
    });
  });

  if (config.mock) {
    store.replaceAll(buildMockOrders());
    // In mock mode there's no live socket, but per §2.9 the badge stays Online.
  } else {
    startLiveRuntime({
      store,
      settings,
      config,
      xApiKey,
      onConnectionChange: (next) => {
        connection = next;
        // Re-render with current snapshot so the badge updates immediately.
        const snap = store.snapshot();
        render({
          connection,
          storeName: settings.storeName,
          left: snap.left,
          right: snap.right,
          loading,
        });
      },
      onBootstrapped: () => {
        loading = false;
      },
    });
  }

  startVersionWatcher(config.appVersionPollMs);
}

void boot().catch((err) => {
  captureException(err, { stage: 'boot-async' });
  renderErrorPanel(
    root!,
    {
      title: 'Unexpected Error',
      body: 'Something went wrong while starting the screen. Please contact crew or IT support.',
      detail: err instanceof Error ? err.message : String(err),
    },
    config.showErrorDetail,
  );
});
